import * as React from "react";
import { useIsFetching } from "@tanstack/react-query";
import { LoaderCircle } from "lucide-react";

/** How long a request may be outstanding before the page admits it is waiting. */
export const SLOW_REQUEST_MS = 10_000;

/**
 * A request that never answers leaves the page showing nothing: fetch has no default timeout, so
 * a stalled connection keeps `isPending` true indefinitely and the route renders its title and
 * no content. Measured on staging by holding every project call open — eight routes showed a
 * heading and a blank body, with no spinner, no message and no way to tell it apart from a bug.
 *
 * This does not abort anything. It only says what is happening once waiting has gone on long
 * enough that silence is misleading, which is why it needs no threshold decision about what
 * counts as "too slow" for a request that might still succeed.
 */
export function SlowRequestNotice({ busy }: { busy: boolean }) {
  const [slow, setSlow] = React.useState(false);

  React.useEffect(() => {
    if (!busy) {
      setSlow(false);
      return;
    }
    const timer = window.setTimeout(() => setSlow(true), SLOW_REQUEST_MS);
    return () => window.clearTimeout(timer);
  }, [busy]);

  if (!slow) return null;

  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground"
    >
      <LoaderCircle className="size-4 shrink-0 animate-spin" aria-hidden />
      This is taking longer than usual. The page will fill in when the server answers; if it does
      not, reloading is safe.
    </div>
  );
}

/**
 * The shell must not subscribe to fetch state itself: `useIsFetching` re-renders its consumer on
 * every fetch transition, and the shell wraps the routed Outlet, so subscribing there replaced
 * the subtree mid-assertion — measured as "element could not be found in the document" on a test
 * that had already found the heading. This component is the subscriber, and it renders the same
 * markup as before.
 */
export function SlowRequestNoticeWhenIdle() {
  const pending = useIsFetching() > 0;
  return <SlowRequestNotice busy={pending} />;
}
